import { ssoService } from '@/services/sso.service';
import { LoginProvider } from '@/types/sso.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Edit,
  PlusCircle,
  Power,
  PowerOff,
  RefreshCw,
  Search,
  Shield,
  TestTube,
  Trash2
} from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import SimpleSelect, { SelectOption } from '@/components/ui/SimpleSelect';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { SSO_CONFIGURATION_PERMISSION } from '@/constants/constant-permissions';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import { extractErrorMessage } from '@/utils/error-helper';

export default function SSOConfiguration() {
  usePermissionRouteAccess([
    SSO_CONFIGURATION_PERMISSION.ADMIN_SSO_CONFIGURATION_READ,
    SSO_CONFIGURATION_PERMISSION.ADMIN_SSO_CONFIGURATION_WRITE,
  ]);
  const canWrite = useHasPermission([SSO_CONFIGURATION_PERMISSION.ADMIN_SSO_CONFIGURATION_WRITE]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEnabled, setFilterEnabled] = useState<string>('');
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<{ id: string; name: string } | null>(null);

  const STATUS_FILTER_OPTIONS: SelectOption[] = [
    { label: 'All Status', value: '' },
    { label: 'Enabled', value: 'true' },
    { label: 'Disabled', value: 'false' },
  ];

  const enabledFilter = filterEnabled === '' ? undefined : filterEnabled === 'true';

  const { data: providersData, isLoading, error } = useQuery({
    queryKey: ['ssoProviders', { search: searchTerm, enabled: enabledFilter }],
    queryFn: () => ssoService.getProviders({ 
      limit: 50,
      enabled: enabledFilter 
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ssoService.deleteProvider(id),
    onSuccess: () => {
      toast.success('SSO Provider deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['ssoProviders'] });
    },
    onError: (error: Error) => {
      toast.error(extractErrorMessage(error, 'Failed to delete SSO provider.'));
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => 
      ssoService.toggleProvider(id, enabled),
    onSuccess: () => {
      toast.success('Provider status updated!');
      queryClient.invalidateQueries({ queryKey: ['ssoProviders'] });
    },
    onError: (error: Error) => {
      toast.error(extractErrorMessage(error, 'Failed to update provider status.'));
    },
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => ssoService.testProvider(id),
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Provider configuration is valid!');
      } else {
        toast.error(`Configuration test failed: ${result.error_details.join(', ')}`);
      }
    },
    onError: (error: Error) => {
      toast.error(extractErrorMessage(error, 'Failed to test provider configuration.'));
    },
  });

  const syncMutation = useMutation({
    mutationFn: (id: string) => ssoService.syncWellKnown(id),
    onSuccess: (result) => {
      if (result) {
        toast.success('Well-known configuration synced successfully!');
        queryClient.invalidateQueries({ queryKey: ['ssoProviders'] });
      }
    },
    onError: (error: Error) => {
      toast.error(extractErrorMessage(error, 'Failed to sync well-known configuration.'));
    },
  });

  const bulkToggleMutation = useMutation({
    mutationFn: ({ ids, enabled }: { ids: string[]; enabled: boolean }) => 
      ssoService.bulkToggleProviders(ids, enabled),
    onSuccess: () => {
      toast.success('Selected providers updated!');
      queryClient.invalidateQueries({ queryKey: ['ssoProviders'] });
      setSelectedProviders([]);
    },
    onError: (error: Error) => {
      toast.error(extractErrorMessage(error, 'Failed to update selected providers.'));
    },
  });

  const handleSelectProvider = (id: string) => {
    setSelectedProviders(prev => 
      prev.includes(id) 
        ? prev.filter(p => p !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedProviders.length === (providersData?.providers.length || 0)) {
      setSelectedProviders([]);
    } else {
      setSelectedProviders(providersData?.providers.map(p => p._id!).filter(Boolean) || []);
    }
  };

  const filteredProviders = providersData?.providers.filter(provider =>
    provider.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    provider.provider_name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (error) toast.error('Failed to fetch SSO providers.');

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8" />
          <h1 className="text-2xl font-bold">SSO Configuration</h1>
        </div>
        <p className="text-blue-100">Manage Single Sign-On providers for enterprise authentication.</p>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search providers..."
               className="input-field pl-10 w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <div className="w-44 flex-shrink-0">
            <SimpleSelect
              value={filterEnabled}
              onChange={(value) => setFilterEnabled(value as string ?? '')}
              options={STATUS_FILTER_OPTIONS}
              placeholder="Filter by status"
              isSearchable={false}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {selectedProviders.length > 0 && (
            <>
              <button
                onClick={() => canWrite && bulkToggleMutation.mutate({ ids: selectedProviders, enabled: true })}
                className={`btn btn-outline btn-sm${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
                disabled={!canWrite || bulkToggleMutation.isPending}
                title={!canWrite ? 'You do not have permission' : undefined}
              >
                <Power className="w-4 h-4" />
                Enable
              </button>
              <button
                onClick={() => canWrite && bulkToggleMutation.mutate({ ids: selectedProviders, enabled: false })}
                className={`btn btn-outline btn-sm${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
                disabled={!canWrite || bulkToggleMutation.isPending}
                title={!canWrite ? 'You do not have permission' : undefined}
              >
                <PowerOff className="w-4 h-4" />
                Disable
              </button>
            </>
          )}
          
          <Link
            to={canWrite ? "/admin/enterprise/sso-configuration/new" : "#"}
            onClick={(e) => !canWrite && e.preventDefault()}
            className={`btn btn-primary inline-flex items-center gap-2${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
            title={!canWrite ? 'You do not have permission' : undefined}
          >
            <PlusCircle className="w-4 h-4" />
            Add Provider
          </Link>
        </div>
      </div>

      {/* Providers Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedProviders.length === filteredProviders.length && filteredProviders.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client ID</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Sign Up</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8">
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Loading providers...
                    </div>
                  </td>
                </tr>
              ) : filteredProviders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500">
                    No SSO providers found.
                  </td>
                </tr>
              ) : (
                filteredProviders.map((provider: LoginProvider) => (
                  <tr key={provider._id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedProviders.includes(provider._id!)}
                        onChange={() => handleSelectProvider(provider._id!)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        {provider.logo_url && (
                          <img 
                            src={provider.logo_url} 
                            alt={provider.provider_name}
                            className="w-6 h-6 object-contain"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                        <div>
                          <div className="font-medium text-gray-900">{provider.display_name}</div>
                          <div className="text-sm text-gray-500">{provider.provider_name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                        {provider.client_id.substring(0, 20)}...
                      </code>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`badge ${provider.enabled ? 'badge-success' : 'badge-gray'}`}>
                        {provider.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-sm font-medium">{provider.priority || '-'}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {provider.allow_signup ? (
                        <span className="text-green-600 text-sm">✓</span>
                      ) : (
                        <span className="text-gray-400 text-sm">✗</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                        {provider.metadata?.provider_type || provider.provider_name}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => canWrite && testMutation.mutate(provider._id!)}
                          className={`p-2 text-gray-400 hover:text-blue-600 rounded-md hover:bg-blue-50${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
                          title={!canWrite ? 'You do not have permission' : 'Test Configuration'}
                          disabled={!canWrite || testMutation.isPending}
                        >
                          <TestTube className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => canWrite && syncMutation.mutate(provider._id!)}
                          className={`p-2 text-gray-400 hover:text-purple-600 rounded-md hover:bg-purple-50${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
                          title={!canWrite ? 'You do not have permission' : 'Sync Well-known'}
                          disabled={!canWrite || syncMutation.isPending}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => canWrite && toggleMutation.mutate({
                            id: provider._id!,
                            enabled: !provider.enabled
                          })}
                          className={`p-2 rounded-md ${
                            provider.enabled
                              ? 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                              : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                          }${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
                          title={!canWrite ? 'You do not have permission' : provider.enabled ? 'Disable' : 'Enable'}
                          disabled={!canWrite || toggleMutation.isPending}
                        >
                          {provider.enabled ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                        </button>

                        <button
                          onClick={() => canWrite && navigate(`/admin/enterprise/sso-configuration/edit/${provider._id}`)}
                          className={`p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-50${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
                          title={!canWrite ? 'You do not have permission' : 'Edit Provider'}
                          disabled={!canWrite}
                        >
                          <Edit className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => {
                            if (!canWrite) return;
                            setProviderToDelete({ id: provider._id!, name: provider.display_name });
                            setShowDeleteModal(true);
                          }}
                          className={`p-2 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50${!canWrite ? ' opacity-50 cursor-not-allowed' : ''}`}
                          title={!canWrite ? 'You do not have permission' : 'Delete Provider'}
                          disabled={!canWrite || deleteMutation.isPending}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer Stats */}
      {providersData && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div>
            Showing {filteredProviders.length} of {providersData.total} providers
          </div>
          <div>
            {providersData.providers.filter(p => p.enabled).length} enabled, {' '}
            {providersData.providers.filter(p => !p.enabled).length} disabled
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setProviderToDelete(null);
        }}
        onConfirm={() => {
          if (providerToDelete) {
            deleteMutation.mutate(providerToDelete.id);
            setShowDeleteModal(false);
            setProviderToDelete(null);
          }
        }}
        title="Delete SSO Provider"
        description="Are you sure you want to delete this SSO provider? This action cannot be undone."
        itemName={providerToDelete?.name}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
